import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, parseISO } from 'date-fns'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function cleanIcsString(str: string | undefined | null) {
  if (!str) return '';
  return str.replace(/\\([,;\\nN])/g, (match, char) => {
    if (char.toLowerCase() === 'n') return '\n';
    return char;
  }).trim();
}

export function safeFormatDate(dateStr: string | undefined | null, formatStr: string, fallback = 'TBD') {
  if (!dateStr) return fallback;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return fallback;
  return format(d, formatStr);
}

export function safeFormatISO(dateStr: string | undefined | null, formatStr: string, fallback = 'TBD') {
  if (!dateStr) return fallback;
  const d = parseISO(dateStr);
  if (isNaN(d.getTime())) return fallback;
  return format(d, formatStr);
}

export function validatePassword(pass: string): string | null {
  if (pass.length < 8) return 'Password must be at least 8 characters long.';
  if (!/[A-Z]/.test(pass)) return 'Password must contain at least one uppercase letter.';
  if (!/[a-z]/.test(pass)) return 'Password must contain at least one lowercase letter.';
  if (!/[0-9]/.test(pass)) return 'Password must contain at least one number.';
  if (!/[^A-Za-z0-9]/.test(pass)) return 'Password must contain at least one special character.';
  return null;
}

export function isZavalaExpired(expireDate?: string): boolean {
  if (!expireDate) return false;
  const localDate = new Date();
  const year = localDate.getFullYear();
  const month = String(localDate.getMonth() + 1).padStart(2, '0');
  const day = String(localDate.getDate()).padStart(2, '0');
  const localDateStr = `${year}-${month}-${day}`;
  return expireDate < localDateStr;
}

export async function sha256Hex(text: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(text);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
}

export function safeUserSearch(u: any, query: string): boolean {
  if (!u) return false;
  const q = (query || '').trim().toLowerCase();
  if (!q) return true;

  const firstName = String(u.firstName || '').toLowerCase();
  const lastName = String(u.lastName || '').toLowerCase();
  const fullName = `${firstName} ${lastName}`.trim();
  const email = String(u.email || '').toLowerCase();
  const sfiEmail = String(u.sfiEmail || '').toLowerCase();
  const scc = String(u.sccNumber || '').toLowerCase();
  const ship = String(u.shipName || '').toLowerCase();
  const rank = String(u.currentRank || '').toLowerCase();
  
  let depts = '';
  if (Array.isArray(u.departments)) {
    depts = u.departments.map(d => String(d || '')).join(' ').toLowerCase();
  } else if (u.department) {
    depts = String(u.department).toLowerCase();
  }

  return (
    fullName.includes(q) ||
    firstName.includes(q) ||
    lastName.includes(q) ||
    email.includes(q) ||
    sfiEmail.includes(q) ||
    scc.includes(q) ||
    ship.includes(q) ||
    rank.includes(q) ||
    depts.includes(q)
  );
}

export function isGuestOrVolunteer(user: any): boolean {
  if (!user) return false;
  const roles = Array.isArray(user.roles) ? user.roles : [];
  if (roles.includes('guest')) return true;
  const ship = String(user.shipName || '').trim().toLowerCase();
  if (ship && !ship.includes('zavala') && !roles.includes('admin')) {
    return true;
  }
  return false;
}

